/**
 * Синхронизация строки адреса доставки с точкой на карте.
 *
 * В окне карты зона доставки и координаты вычисляются по ТОЧКЕ: центр карты, адрес из
 * «Моих адресов», геолокация. Строку адреса пользователь может ещё и набрать руками — и
 * ручной ввод меняет только строку. Пока набранное не найдено геокодером, зона и
 * координаты принадлежат прежней точке: у нового клиента это филиал, на котором
 * открывается карта. Сохранение в этот момент отправляло заказ с адресом из хутора вне
 * всех зон в зону филиала и в филиал (заказ #8300 из хутора Копанской ушёл в «Широкую 32»).
 *
 * Композабл хранит строку, для которой точка подтверждена, и следит за запросами точки,
 * чтобы сохранение дожидалось их, а устаревший ответ геокодера не перетирал свежий.
 */
import { computed, ref } from 'vue'

const normalizeAddress = (value: string | null | undefined): string => (value ?? '').trim()

export function useAddressPointSync(getAddress: () => string | null | undefined) {
  /** Строка адреса, записанная вместе с точкой (зоной и координатами). */
  const confirmedAddress = ref<string | null>(null)
  /** Геокодер не нашёл набранный адрес — точки у него нет. */
  const addressNotFound = ref(false)

  const isAddressUnconfirmed = computed(
    () => normalizeAddress(getAddress()) !== normalizeAddress(confirmedAddress.value),
  )

  let latestPointRequest = 0
  const pendingPoints = new Set<Promise<unknown>>()
  let typedLookup: { address: string; task: Promise<void> } | null = null

  /**
   * Начинает новый запрос точки. Ответ любого более раннего запроса после этого
   * считается устаревшим: пока геокодер отвечал, пользователь уже сдвинул карту
   * или выбрал другой адрес.
   */
  function beginPointRequest(): { isStale: () => boolean } {
    const id = ++latestPointRequest
    return { isStale: () => id !== latestPointRequest }
  }

  /** Регистрирует запрос точки, которого должно дождаться сохранение. */
  function trackPoint<T>(task: Promise<T>): Promise<T> {
    pendingPoints.add(task)
    task.then(
      () => pendingPoints.delete(task),
      () => pendingPoints.delete(task),
    )
    return task
  }

  /**
   * Ищет набранную строку. Повторный вызов для той же строки (потеря фокуса, а сразу
   * за ней нажатие «Сохранить») не запускает второй поиск, а ждёт первый.
   * `isCurrent` в поиске отвечает, не поменял ли пользователь строку, пока геокодер думал.
   */
  function lookupTypedAddress(
    lookup: (address: string, isCurrent: () => boolean) => Promise<void>,
  ): Promise<void> {
    const address = normalizeAddress(getAddress())
    if (!address || !isAddressUnconfirmed.value) return Promise.resolve()
    if (typedLookup?.address === address) return typedLookup.task

    const isCurrent = () => normalizeAddress(getAddress()) === address
    const task = trackPoint(lookup(address, isCurrent)).finally(() => {
      if (typedLookup?.task === task) typedLookup = null
    })
    typedLookup = { address, task }
    return task
  }

  /** Дожидается всех запросов точки, включая начатые во время ожидания. */
  async function waitForPendingPoints(): Promise<void> {
    while (pendingPoints.size > 0) {
      await Promise.allSettled(pendingPoints)
    }
  }

  /** Строка и точка записаны вместе. */
  function confirmAddress(address: string): void {
    confirmedAddress.value = address
    addressNotFound.value = false
  }

  function markAddressNotFound(): void {
    addressNotFound.value = true
  }

  /** Пользователь правит строку — прежний «не нашли» к новой строке не относится. */
  function onAddressTyped(): void {
    addressNotFound.value = false
  }

  return {
    addressNotFound,
    isAddressUnconfirmed,
    beginPointRequest,
    trackPoint,
    lookupTypedAddress,
    waitForPendingPoints,
    confirmAddress,
    markAddressNotFound,
    onAddressTyped,
  }
}
